import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { ApiError } from '../../api/client';
import type { Session } from '../../api/session';
import { createPass, createPassRequest, type NewPass, type ObjectDetails } from '../../api/tenant';
import type { Duration, Mode } from '../../domain/availability';
import { formatServerDate } from '../../domain/moscowTime';
import { describePassError } from '../../domain/passErrors';
import { GuestType } from '../../domain/passes';
import { normalizePlate } from '../../domain/plate';
import { passWindow } from '../../domain/schedule';
import type { TelegramApi } from '../../telegram/webapp';

export type HomeGuest = { kind: 'vehicle'; plate: string } | { kind: 'pedestrian'; name: string };

export interface HomeOrderDone {
  request: boolean;
  passId: number | null;
  guest: HomeGuest;
  expiresAt: Date | null;
  addressName: string;
}

interface Deps {
  session: Session;
  telegram: TelegramApi;
  details: ObjectDetails;
  address: { id: number; name: string };
}

const REQUEST_ONLY = 'Этот пропуск на объекте оформляется через согласование с УК — нажмите ещё раз';

/**
 * «Заказать пропуск» from the home screen and from a saved guest: today, from now until 23:59 (or the object
 * limit), with the same rules as the full form. A one-time pass can turn out to need approval only at the server
 * (PASS_TYPE_ALLOWED_BY_REQUEST_ONLY): then the next tap sends a request.
 */
export function useHomeOrder({ session, telegram, details, address }: Deps) {
  const queryClient = useQueryClient();
  const [done, setDone] = useState<HomeOrderDone | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [forceRequest, setForceRequest] = useState(false);
  const busy = useRef(false);

  const submit = useMutation({
    mutationFn: async ({ guest, purposeId, plan }: { guest: HomeGuest; purposeId: number | null; plan: { duration: Duration; mode: Mode } }) => {
      const request = forceRequest || plan.mode === 'request';
      const limit = details.settings[plan.duration === 'temporary' ? 'passesLimitHoursTemporary' : 'passesLimitHoursOneTime'];
      const window = passWindow({ when: 'now', duration: plan.duration, limitHours: typeof limit === 'number' && limit > 0 ? limit : null, now: new Date(), forRequest: request });
      const clean: HomeGuest = guest.kind === 'vehicle' ? { kind: 'vehicle', plate: normalizePlate(guest.plate) } : { kind: 'pedestrian', name: guest.name.trim() };
      const body: NewPass = {
        addressId: address.id,
        durationType: plan.duration === 'temporary' ? 2 : 1,
        guestType: clean.kind === 'vehicle' ? GuestType.Vehicle : GuestType.Pedestrian,
        guestData: clean.kind === 'vehicle' ? { plateNumber: clean.plate } : { name: clean.name },
        ...(window.startsAt ? { startsAt: formatServerDate(window.startsAt) } : {}),
        ...(window.expiresAt ? { expiresAt: formatServerDate(window.expiresAt) } : {}),
        ...(purposeId ? { options: [purposeId] } : {}),
      };
      let passId: number | null = null;
      if (request) await createPassRequest(session, body);
      else passId = (await createPass(session, body))?.id ?? null;
      return { request, passId, guest: clean, expiresAt: window.expiresAt, addressName: address.name };
    },
    onSuccess: (result) => {
      telegram.notify('success');
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['passes'] });
      setDone(result);
    },
    onError: (e) => {
      telegram.notify('error');
      if (e instanceof ApiError && e.code === 'PASS_TYPE_ALLOWED_BY_REQUEST_ONLY') {
        setForceRequest(true);
        setError(REQUEST_ONLY);
        return;
      }
      const view = describePassError(e);
      setError(view.fields.plate ?? view.fields.name ?? view.general ?? 'Не получилось оформить. Попробуйте ещё раз');
    },
    onSettled: () => {
      busy.current = false;
    },
  });

  return {
    order(guest: HomeGuest, purposeId: number | null, plan: { duration: Duration; mode: Mode }) {
      if (busy.current) return;
      busy.current = true;
      submit.mutate({ guest, purposeId, plan });
    },
    pending: submit.isPending,
    done,
    error,
    forceRequest,
    reset() {
      setDone(null);
      setError(null);
    },
  };
}
